import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardBody, Input, PageHeader } from "../../shared/ui";
import { DELIVERY_CHAPTER } from "./guideDelivery";
import { RELEASE_CHAPTER } from "./guideReleases";
import { ORGANISATION_CHAPTER } from "./guideOrganisation";
import type { GuideChapter, GuideSection } from "./guideTypes";

const CHAPTERS: GuideChapter[] = [
  DELIVERY_CHAPTER,
  RELEASE_CHAPTER,
  ORGANISATION_CHAPTER,
];

/** Everything in a section as one lower-case string, for the filter. */
function haystack(section: GuideSection): string {
  return [
    section.title,
    section.where,
    section.purpose,
    ...section.examples.flatMap((example) => [example.title, ...example.steps]),
    ...section.tables.flatMap((table) => [
      table.title,
      table.intro ?? "",
      ...table.options.flatMap((option) => [option.name, option.meaning]),
    ]),
    ...(section.notes ?? []),
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * The user guide (§67).
 *
 * <p>A page rather than a tour or a set of tooltips. Tooltips answer "what is this field" for
 * somebody already looking at it; the questions people actually arrive with are "what is this
 * screen for" and "which of these options do I want", and neither fits in a tooltip.
 *
 * <p><strong>The option tables are built from the application's own vocabulary.</strong> Adding a
 * status to the product makes this page fail to compile until somebody writes what it means —
 * which is the only way a guide stays true. A guide that has quietly gone stale is worse than
 * none, because people stop trusting the parts that are still right.
 *
 * <p>Every section is deep-linkable, so an answer can be sent to somebody rather than described.
 */
export function UserGuidePage() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const contentRef = useRef<HTMLDivElement | null>(null);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === "") {
      return CHAPTERS;
    }
    return CHAPTERS.map((chapter) => ({
      ...chapter,
      sections: chapter.sections.filter((section) => haystack(section).includes(needle)),
    })).filter((chapter) => chapter.sections.length > 0);
  }, [query]);

  // Deep links: /guide?s=readiness scrolls to that section rather than the top of the page.
  useEffect(() => {
    const target = params.get("s");
    if (!target) {
      return;
    }
    const element = document.getElementById(`guide-${target}`);
    element?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [params]);

  const jump = (id: string) => {
    setParams({ s: id }, { replace: true });
    document.getElementById(`guide-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const total = filtered.reduce((count, chapter) => count + chapter.sections.length, 0);

  return (
    <>
      <PageHeader
        title="User guide"
        subtitle="What each part of the application is for, and what every option means."
      />

      <div className="mb-4 max-w-md">
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search the guide — try 'freeze', 'waiver', 'timesheet'"
          aria-label="Search the guide"
        />
      </div>

      {query.trim() !== "" && (
        <p className="mb-4 text-sm text-ink-600">
          {total === 0
            ? "Nothing matches that."
            : `${total} ${total === 1 ? "section" : "sections"} match.`}
        </p>
      )}

      <div className="flex gap-6">
        {/*
          Contents on the left rather than at the top. The guide is long, and a reader who has
          scrolled to Execution should still be able to see that Rollback is the next thing —
          a table of contents you have to scroll back up to is one nobody uses twice.
        */}
        <nav className="hidden w-56 shrink-0 lg:block">
          <div className="sticky top-4 space-y-4">
            {filtered.map((chapter) => (
              <div key={chapter.id}>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-400">
                  {chapter.title}
                </p>
                <ul className="space-y-0.5">
                  {chapter.sections.map((section) => (
                    <li key={section.id}>
                      <button
                        type="button"
                        onClick={() => jump(section.id)}
                        className="w-full rounded px-2 py-1 text-left text-sm text-ink-600 transition-colors hover:bg-ink-100 hover:text-ink-900"
                      >
                        {section.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </nav>

        <div ref={contentRef} className="min-w-0 flex-1 space-y-6">
          {filtered.map((chapter) => (
            <section key={chapter.id} className="space-y-4">
              <h2 className="font-display text-lg font-semibold text-ink-900">
                {chapter.title}
              </h2>

              {chapter.sections.map((section) => (
                <Card key={section.id}>
                  <div
                    id={`guide-${section.id}`}
                    className="scroll-mt-4 border-b border-ink-100 px-4 py-3"
                  >
                    <h3 className="font-display text-base font-semibold text-ink-900">
                      {section.title}
                    </h3>
                    <p className="mt-0.5 text-xs text-ink-500">{section.where}</p>
                  </div>

                  <CardBody className="space-y-5">
                    <p className="text-sm leading-relaxed text-ink-800">{section.purpose}</p>

                    {section.examples.map((example) => (
                      <div
                        key={example.title}
                        className="rounded-lg border-l-2 border-brand-300 bg-brand-50/50 px-3 py-2.5"
                      >
                        <p className="text-sm font-medium text-ink-900">{example.title}</p>
                        <ol className="mt-1.5 space-y-1.5">
                          {example.steps.map((step, index) => (
                            <li key={step} className="flex gap-2 text-sm text-ink-700">
                              <span className="tnum shrink-0 text-xs text-ink-400">
                                {index + 1}.
                              </span>
                              <span className="leading-relaxed">{step}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    ))}

                    {section.tables.map((table) => (
                      <div key={table.title}>
                        <p className="text-sm font-medium text-ink-900">{table.title}</p>
                        {table.intro && (
                          <p className="mt-0.5 text-xs leading-relaxed text-ink-600">
                            {table.intro}
                          </p>
                        )}
                        <dl className="mt-2 divide-y divide-ink-100 rounded-lg border border-ink-200">
                          {table.options.map((option) => (
                            <div
                              key={option.name}
                              className="flex flex-col gap-0.5 px-3 py-2 sm:flex-row sm:gap-4"
                            >
                              <dt className="shrink-0 text-sm font-medium text-ink-900 sm:w-52">
                                {option.name}
                              </dt>
                              <dd className="text-sm leading-relaxed text-ink-700">
                                {option.meaning}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    ))}

                    {section.notes && section.notes.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-ink-900">Worth knowing</p>
                        <ul className="mt-1.5 space-y-1.5">
                          {section.notes.map((note) => (
                            <li key={note} className="flex gap-2 text-sm text-ink-700">
                              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-ink-400" />
                              <span className="leading-relaxed">{note}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardBody>
                </Card>
              ))}
            </section>
          ))}
        </div>
      </div>
    </>
  );
}
